import React, { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router'

import { BenchmarkingFeedView } from 'features/trend-detection/features/benchmarking-feed/components/BenchmarkingFeedView'
import { useBenchmarkingFeed } from 'features/trend-detection/features/benchmarking-feed/hooks/use-benchmarking-feed'
import { useBenchmarkingFeedService } from 'features/trend-detection/features/benchmarking-feed/service'
import {
  BENCHMARKING_FEED_AGENT_TYPE,
  BenchmarkingFeedAction,
  ResearchAgentsAction,
  trackBenchmarkingFeed,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import { trendDetectionBenchmarkingInsightPath } from 'features/trend-detection/paths'
import type {
  BenchmarkingFeedDirection,
  BenchmarkingFeedPeriod,
  BenchmarkingInsightFeedItem,
} from 'features/trend-detection/types'

export interface BenchmarkingFeedStats {
  totalCount: number
  laggingCount: number
  leadingCount: number
  isLoading: boolean
}

export interface BenchmarkingFeedContainerProps {
  projectId: string | null
  enabled: boolean
  onStats?: (stats: BenchmarkingFeedStats) => void
  /** Deep-link seeds (agents list "View latest/earlier" CTA). */
  initialAgentUuidFilter?: string | null
  initialPeriod?: BenchmarkingFeedPeriod
}

export const BenchmarkingFeedContainer: React.FC<
  BenchmarkingFeedContainerProps
> = ({
  projectId,
  enabled,
  onStats,
  initialAgentUuidFilter,
  initialPeriod,
}) => {
  const navigate = useNavigate()
  const service = useBenchmarkingFeedService()
  const feed = useBenchmarkingFeed({
    service,
    projectId,
    enabled,
    initialAgentUuidFilter,
    initialPeriod,
  })

  useEffect(() => {
    if (!onStats) return
    onStats({
      totalCount: feed.totalCount,
      laggingCount: feed.underperformsCount,
      leadingCount: feed.outperformsCount,
      isLoading: feed.isLoading,
    })
  }, [
    feed.totalCount,
    feed.outperformsCount,
    feed.underperformsCount,
    feed.isLoading,
    onStats,
  ])

  const handleAgentFilterChange = useCallback(
    (uuid: string | null) => {
      trackBenchmarkingFeed(BenchmarkingFeedAction.AgentFilterChange, {
        agent_type: BENCHMARKING_FEED_AGENT_TYPE,
        agent_uuid: uuid ?? 'all',
      })
      feed.setAgentUuidFilter(uuid)
    },
    [feed],
  )

  const handleDirectionChange = useCallback(
    (direction: BenchmarkingFeedDirection | null) => {
      trackBenchmarkingFeed(BenchmarkingFeedAction.DirectionChange, {
        agent_type: BENCHMARKING_FEED_AGENT_TYPE,
        direction: direction ?? 'all',
      })
      feed.setDirectionFilter(direction)
    },
    [feed],
  )

  const handlePeriodChange = useCallback(
    (next: BenchmarkingFeedPeriod) => {
      trackBenchmarkingFeed(BenchmarkingFeedAction.PeriodChange, {
        agent_type: BENCHMARKING_FEED_AGENT_TYPE,
        period: next,
        source:
          next === 'all_time' && feed.period === 'current'
            ? 'view_previous_banner'
            : 'toolbar',
      })
      feed.setPeriod(next)
    },
    [feed],
  )

  const handlePageChange = useCallback(
    (page: number) => {
      trackBenchmarkingFeed(BenchmarkingFeedAction.PageChange, {
        agent_type: BENCHMARKING_FEED_AGENT_TYPE,
        page,
      })
      feed.onPageChange(page)
    },
    [feed],
  )

  const handleInsightClick = useCallback(
    (insight: BenchmarkingInsightFeedItem) => {
      if (!projectId) return
      trackResearchAgents(ResearchAgentsAction.benchmarking_insight_click, {
        insight_uuid: insight.uuid,
        insight_title: insight.title,
        agent_uuid: insight.agent_uuid,
        agent_name: insight.agent_name,
        source: 'performance_gaps',
      })
      navigate(trendDetectionBenchmarkingInsightPath(projectId, insight.uuid))
    },
    [navigate, projectId],
  )

  if (!enabled) return null

  return (
    <BenchmarkingFeedView
      projectId={projectId}
      items={feed.items}
      totalCount={feed.totalCount}
      totalPages={feed.totalPages}
      page={feed.page}
      isLoading={feed.isLoading}
      hasHistoricalInsights={feed.hasHistoricalInsights}
      agentsForFilter={feed.agentsForFilter}
      agentUuidFilter={feed.agentUuidFilter}
      onAgentUuidFilterChange={handleAgentFilterChange}
      directionFilter={feed.directionFilter}
      onDirectionFilterChange={handleDirectionChange}
      period={feed.period}
      onPeriodChange={handlePeriodChange}
      onPageChange={handlePageChange}
      onInsightClick={handleInsightClick}
    />
  )
}
